import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Transition } from "@headlessui/react";

import useTheme from "hooks/useTheme";

import TransactionPayDetailModal from "theme/modals/transactions/TransactionPayDetailModal";
import TransactionAssetTransferDetailModal from "theme/modals/transactions/TransactionAssetTransferDetailModal";
import TransactionAppCallDetailModal from "theme/modals/transactions/TransactionAppCallDetailModal";

import CoinReceivedIcon from "theme/icons/CoinsReceived";
import CoinSentIcon from "theme/icons/CoinsSent";
import OptInAsaIcon from "theme/icons/OptInASA";
import TransactionsLoader from "theme/loaders/dashboard/TransactionsLoader";
import AlgoExplorerIcon from "theme/icons/AlgoExplorer";
import useAccountTransactions from "hooks/accounts/useAccountTransactions";
import useRefresh from "hooks/useRefresh";
import StyledTooltipWithoutArrow from "theme/tooltips/StyledTooltipWithoutArrow";

const Transactions = () => {
	const observer = useRef();
	const { appLanguage, themeColor } = useTheme();
	const { isRefresh } = useRefresh();

	const accounts = useSelector((state) => state.Accounts);
	const contacts = useSelector((state) => state.Contacts);
	const accountInfo = useSelector((state) => state.AccountInfo);
	const algoChain = useSelector((state) => state.AppPreferences.algoChain);

	const [pageNum, setPageNum] = useState(1);

	const [transactionPayDetailModalIsOpen, setTransactionPayDetailModalIsOpen] = useState(false);
	const [transactionPayDetailModalInfo, setTransactionPayDetailModalInfo] = useState(null);

	const [transactionAssetTransferDetailModalIsOpen, setTransactionAssetTransferDetailModalIsOpen] = useState(false);
	const [transactionAssetTransferDetailModalInfo, setTransactionAssetTransferDetailModalInfo] = useState(null);

	const [transactionAppCallDetailModalIsOpen, setTransactionAppCallDetailModalIsOpen] = useState(false);
	const [transactionAppCallDetailModalInfo, setTransactionAppCallDetailModalInfo] = useState(null);

	const { isLoading, error, transactions, hasMore, refreshTransactions } = useAccountTransactions(
		accountInfo?.address,
		pageNum,
		10,
		algoChain
	);

	const lastBookElementRef = useCallback(
		(node) => {
			if (isLoading) return;
			if (observer.current) observer.current.disconnect();
			observer.current = new IntersectionObserver((entries) => {
				if (entries[0].isIntersecting && hasMore) {
					setPageNum((prev) => prev + 1);
				}
			});
			if (node) observer.current.observe(node);
		},
		[isLoading, hasMore]
	);

	const getAccountName = (accountAddress) => {
		const contact = contacts.find((contact) => contact?.["account-address"] === accountAddress);
		const account = accounts.find((account) => account?.["account-address"] === accountAddress);

		if (contact?.["friendly-name"]) return contact["friendly-name"];
		else if (account?.name) return account.name;
		else return accountAddress.slice(0, 5) + "..." + accountAddress.slice(-5);
	};

	useEffect(() => {
		if (isRefresh || accountInfo?.address) {
			setPageNum(1);
			refreshTransactions();
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isRefresh, accountInfo]);

	return (
		<Transition
			as={Fragment}
			appear={true}
			show={true}
			enter="transition-opacity duration-500"
			enterFrom="opacity-0"
			enterTo="opacity-100"
		>
			<div className="flex flex-col w-full pb-5">
				{/* <div className="flex w-full justify-end px-5 pb-2">
					<RefreshIcon
						onClick={() => {
							setPageNum(1);
							refreshTransactions();
						}}
						className={`w-4 ${
							!transactions?.length && pageNum === 1 && "animate-spin"
						} cursor-pointer smooth hover:shadow-xl`}
						fill={themeColor?.hex}
					/>
				</div> */}
				{transactions?.map((accountTransaction, index) => (
					<div
						state={{ transactionID: accountTransaction?.id }}
						className={`flex w-full cursor-pointer space-x-2 pr-5 pl-3 py-2 rounded-lg ${themeColor.bg} bg-opacity-0 hover:bg-opacity-10`}
						key={index}
						ref={lastBookElementRef}
					>
						{accountTransaction["tx-type"] === "pay" ? (
							accountTransaction["payment-transaction"]["receiver"] === accountTransaction.sender ||
							accountTransaction.sender !== accountInfo?.address ? (
								<div
									className="flex w-full items-center justify-between"
									onClick={() => {
										setTransactionPayDetailModalInfo(accountTransaction);
										setTransactionPayDetailModalIsOpen(true);
									}}
								>
									<div className="flex items-start space-x-8">
										<CoinReceivedIcon className={`w-6`} fill={themeColor?.hex} />
										<div className="flex-col">
											<p className={`text-sm font-bold ${themeColor?.text}`}>{appLanguage?.received}</p>
											<p className={`text-sm font-thin ${themeColor?.text}`}>
												{accountTransaction?.["payment-transaction"]?.receiver &&
													getAccountName(accountTransaction?.sender)}
											</p>
										</div>
									</div>

									<div className="flex flex-col w-fit items-end justify-end">
										<p className={`text-sm font-bold ${themeColor?.text}`}>
											{(accountTransaction["payment-transaction"]["amount"] / 1000000).toLocaleString(
												undefined,
												{
													maximumFractionDigits: 6,
												}
											)}{" "}
											Algo
										</p>
										{/* <p className={`text-sm font-thin ${themeColor?.text}`}>0$</p> */}
									</div>
								</div>
							) : (
								<div
									className="flex w-full items-center justify-between"
									onClick={() => {
										setTransactionPayDetailModalInfo(accountTransaction);
										setTransactionPayDetailModalIsOpen(true);
									}}
								>
									<div className="flex items-start space-x-8">
										<CoinSentIcon className={`w-6`} fill="#FC4242" />
										<div className="flex-col">
											<p className={`text-sm font-bold text-candy-red`}>{appLanguage?.sent}</p>
											<p className={`text-sm font-thin ${themeColor?.text}`}>
												{accountTransaction?.["payment-transaction"]?.receiver &&
													getAccountName(accountTransaction?.["payment-transaction"]?.receiver)}
											</p>
										</div>
									</div>

									<div className="flex flex-col w-fit items-end justify-end">
										<p className={`text-sm font-bold text-candy-red`}>
											-
											{(accountTransaction["payment-transaction"]["amount"] / 1000000).toLocaleString(
												undefined,
												{
													maximumFractionDigits: 6,
												}
											)}{" "}
											Algo
										</p>
										{/* <p className={`text-sm font-thin ${themeColor?.text}`}>0$</p> */}
									</div>
								</div>
							)
						) : accountTransaction["tx-type"] === "axfer" ? (
							accountTransaction["asset-transfer-transaction"]["receiver"] === accountTransaction.sender ||
							accountTransaction.sender !== accountInfo?.address ? (
								<div
									className="flex w-full items-center justify-between"
									onClick={() => {
										setTransactionAssetTransferDetailModalInfo(accountTransaction);
										setTransactionAssetTransferDetailModalIsOpen(true);
									}}
								>
									<div className="flex items-start space-x-8">
										<CoinReceivedIcon className={`w-6`} fill={themeColor?.hex} />
										<div className="flex-col">
											<p className={`text-sm font-bold ${themeColor?.text}`}>{appLanguage?.received}</p>
											<p className={`text-sm font-thin ${themeColor?.text}`}>
												{accountTransaction?.["asset-transfer-transaction"]?.receiver &&
													getAccountName(accountTransaction?.sender)}
											</p>
										</div>
									</div>

									<div className="flex flex-col w-fit items-end justify-end">
										<p className={`text-sm font-bold ${themeColor?.text}`}>
											{(
												accountTransaction["asset-transfer-transaction"]["amount"] /
												Math.pow(10, accountTransaction["asset-transfer-transaction"]["asset-decimals"])
											).toLocaleString(undefined, {
												maximumFractionDigits:
													accountTransaction["asset-transfer-transaction"]["asset-decimals"],
											}) +
												" " +
												accountTransaction["asset-transfer-transaction"]["asset-unit-name"]}
										</p>
										{/* <p className={`text-sm font-thin ${themeColor?.text}`}>0$</p> */}
									</div>
								</div>
							) : (
								<div
									className="flex w-full items-center justify-between"
									onClick={() => {
										setTransactionAssetTransferDetailModalInfo(accountTransaction);
										setTransactionAssetTransferDetailModalIsOpen(true);
									}}
								>
									<div className="flex items-start space-x-8">
										<CoinSentIcon className={`w-6`} fill="#FC4242" />
										<div className="flex-col">
											<p className={`text-sm font-bold text-candy-red`}>{appLanguage?.sent}</p>
											<p className={`text-sm font-thin ${themeColor?.text}`}>
												{accountTransaction?.["asset-transfer-transaction"]?.receiver &&
													getAccountName(accountTransaction["asset-transfer-transaction"].receiver)}
											</p>
										</div>
									</div>

									<div className="flex flex-col w-fit items-end justify-end">
										<p className={`text-sm font-bold text-candy-red`}>
											-
											{(
												accountTransaction["asset-transfer-transaction"]["amount"] /
												Math.pow(10, accountTransaction["asset-transfer-transaction"]["asset-decimals"])
											).toLocaleString(undefined, {
												maximumFractionDigits:
													accountTransaction["asset-transfer-transaction"]["asset-decimals"],
											})}{" "}
											{accountTransaction["asset-transfer-transaction"]["asset-unit-name"]}
										</p>
										{/* <p className={`text-sm font-thin ${themeColor?.text}`}>0$</p> */}
									</div>
								</div>
							)
						) : (
							accountTransaction["tx-type"] === "appl" && (
								<div
									className="flex w-full items-center justify-between"
									onClick={() => {
										setTransactionAppCallDetailModalInfo(accountTransaction);
										setTransactionAppCallDetailModalIsOpen(true);
									}}
								>
									<div className="flex items-start space-x-8">
										<OptInAsaIcon className={`w-6`} fill={themeColor?.hex} />
										<div className="flex-col">
											<p className={`text-sm font-bold ${themeColor?.text}`}>{appLanguage?.appCall}</p>
											<p className={`text-sm font-thin ${themeColor?.text}`}>
												{accountTransaction?.sender && getAccountName(accountTransaction?.sender)}
											</p>
										</div>
									</div>

									<div className="flex flex-col w-fit items-end justify-end">
										<p className={`text-sm font-bold ${themeColor?.text}`}>
											{accountTransaction["inner-txns"] &&
												accountTransaction["inner-txns"].length + "	inner txns"}
										</p>
										{/* <p className={`text-sm font-thin ${themeColor?.text}`}>-</p> */}
									</div>
								</div>
							)
						)}
						{accountTransaction["tx-type"] !== "appl" && (
							<StyledTooltipWithoutArrow
								title={appLanguage?.algoExplorer}
								children={
									<AlgoExplorerIcon
										className={`w-4 cursor-pointer`}
										fill={themeColor?.hex}
										onClick={() =>
											window.open(`https://algoexplorer.io/tx/${accountTransaction?.id}`, "_blank")
										}
									/>
								}
							/>
						)}
					</div>
				))}
				{isLoading && <TransactionsLoader />}
				{error && "Error..."}

				<TransactionPayDetailModal
					isOpen={transactionPayDetailModalIsOpen}
					setIsOpen={setTransactionPayDetailModalIsOpen}
					modalInfo={transactionPayDetailModalInfo}
				/>

				<TransactionAssetTransferDetailModal
					isOpen={transactionAssetTransferDetailModalIsOpen}
					setIsOpen={setTransactionAssetTransferDetailModalIsOpen}
					modalInfo={transactionAssetTransferDetailModalInfo}
				/>

				<TransactionAppCallDetailModal
					isOpen={transactionAppCallDetailModalIsOpen}
					setIsOpen={setTransactionAppCallDetailModalIsOpen}
					modalInfo={transactionAppCallDetailModalInfo}
				/>
			</div>
		</Transition>
	);
};

export default Transactions;
